"use client";

import { ErrorBoundary as SentryErrorBoundary } from "@sentry/nextjs";
import { ReactNode, useEffect, useState } from "react";
import { Button } from "@/components/ui/primitives/button";
import {
  applyChunkLoadDiagnosticsToScope,
  installChunkLoadDiagnostics,
  scheduleChunkLoadRecovery,
  type ChunkLoadRecoveryResult,
} from "@/lib/chunk-load-diagnostics";

installChunkLoadDiagnostics();

interface ErrorFallbackProps {
  error: unknown;
  componentStack: string;
  eventId: string;
  resetError: () => void;
}

function ErrorFallback({ error, resetError }: ErrorFallbackProps) {
  const [chunkLoadRecovery, setChunkLoadRecovery] =
    useState<ChunkLoadRecoveryResult | null>(null);

  useEffect(() => {
    setChunkLoadRecovery(scheduleChunkLoadRecovery(error));
  }, [error]);

  const errorMessage = error instanceof Error ? error.message : String(error);
  const isChunkLoadError = chunkLoadRecovery?.isChunkLoadError ?? false;
  const scheduledReload = chunkLoadRecovery?.scheduledReload ?? false;

  const primaryAction = isChunkLoadError
    ? {
        label: scheduledReload ? "Refreshing..." : "Refresh page",
        onClick: () => window.location.reload(),
      }
    : {
        label: "Try again",
        onClick: resetError,
      };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background">
      <div className="mx-auto max-w-md text-center">
        <div className="mb-4">
          <h1 className="text-2xl font-bold ">An unexpected error occurred</h1>
        </div>

        <div className="mb-4 rounded-lg bg-[var(--error)]/10 p-4">
          <p className="text-sm text-orange-500">
            <strong>Error:</strong> {errorMessage}
          </p>
        </div>

        {scheduledReload ? (
          <p className="mb-4 text-sm text-muted-foreground">
            Refreshing the page once to recover from a temporary app load issue.
          </p>
        ) : null}

        <div className="flex space-x-3">
          <Button
            onClick={primaryAction.onClick}
            variant="outline"
            className="flex-1"
          >
            {primaryAction.label}
          </Button>

          <Button
            onClick={() => (window.location.href = "/")}
            variant="default"
            className="flex-1"
          >
            Go to homepage
          </Button>
        </div>

        <p className="mt-4 text-sm text-gray-500">
          We&apos;re already working on fixing it. <br />
          If the problem persists, please{" "}
          <a
            href="https://t.me/+XNYpp_-sB5EzMmQy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 dark:text-blue-400 hover:underline"
          >
            contact support
          </a>{" "}
        </p>
      </div>
    </div>
  );
}

interface AppErrorBoundaryProps {
  children: ReactNode;
}

export function AppErrorBoundary({ children }: AppErrorBoundaryProps) {
  return (
    <SentryErrorBoundary
      fallback={ErrorFallback}
      showDialog={false}
      beforeCapture={(scope, error) => {
        scope.setTag("errorBoundary", "AppErrorBoundary");
        applyChunkLoadDiagnosticsToScope(scope, error);
      }}
    >
      {children}
    </SentryErrorBoundary>
  );
}
